import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { CrossCompanyRow } from '../../../types/inventory';

interface CrossCompanyChartProps {
    data: CrossCompanyRow[];
    isLoading?: boolean;
}

function formatNumber(value: number): string {
    return new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 }).format(value);
}

function formatCompact(value: number): string {
    if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
    return value.toFixed(0);
}

// Deterministic palette keyed on company code so each company keeps its color
const COMPANY_COLORS: Record<string, string> = {
    bin: '#3b82f6',
    dch: '#10b981',
    dit: '#f59e0b',
    dfr: '#8b5cf6',
    dsp: '#ec4899',
    dtr: '#06b6d4',
    dpt: '#84cc16',
    dbi: '#f43f5e',
};
const FALLBACK = '#64748b';

interface TooltipPayload {
    active?: boolean;
    payload?: Array<{ payload: CrossCompanyRow }>;
}

function CrossCompanyTooltip({ active, payload }: TooltipPayload) {
    if (!active || !payload || payload.length === 0) return null;
    const row = payload[0].payload;
    return (
        <div className="rounded-lg border border-gray-200 bg-white p-2.5 text-xs shadow-lg">
            <div className="mb-1 font-semibold text-gray-900">
                {row.productName}
            </div>
            <div className="mb-2 text-gray-500">
                Company: <span className="font-medium text-gray-700">{row.company.toUpperCase()}</span>
            </div>
            <div className="space-y-0.5">
                <div className="flex justify-between gap-4">
                    <span className="text-gray-500">Current Stock:</span>
                    <span className="font-mono text-gray-900">{formatNumber(row.currentStockQty)}</span>
                </div>
                <div className="flex justify-between gap-4">
                    <span className="text-gray-500">Stock Value:</span>
                    <span className="font-mono text-gray-900">€{formatNumber(row.currentStockValueEur)}</span>
                </div>
                <div className="flex justify-between gap-4">
                    <span className="text-gray-500">12M Sales Qty:</span>
                    <span className="font-mono text-gray-900">{formatNumber(row.sales12MQty)}</span>
                </div>
                <div className="flex justify-between gap-4">
                    <span className="text-gray-500">12M Revenue:</span>
                    <span className="font-mono text-gray-900">€{formatNumber(row.sales12MRevenueEur)}</span>
                </div>
            </div>
        </div>
    );
}

export function CrossCompanyChart({ data, isLoading }: CrossCompanyChartProps) {
    if (isLoading) {
        return (
            <div className="flex h-48 items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            </div>
        );
    }

    if (!data || data.length === 0) {
        return <div className="py-8 text-center text-sm text-gray-400">No matching products found in other companies.</div>;
    }

    // Dynamic height: ~40px per row + padding
    const height = Math.max(200, data.length * 42 + 40);

    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={data} layout="vertical" margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis
                    type="number"
                    tickFormatter={formatCompact}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                />
                <YAxis
                    type="category"
                    dataKey="productName"
                    tick={{ fontSize: 11, fill: '#334155' }}
                    width={180}
                    tickFormatter={(v: string) => v.length > 28 ? v.slice(0, 26) + '…' : v}
                />
                <Tooltip content={<CrossCompanyTooltip />} cursor={{ fill: '#f1f5f9' }} />
                <Bar dataKey="sales12MQty" name="12M Sales Qty" radius={[0, 4, 4, 0]}>
                    {data.map((row, i) => (
                        <Cell key={i} fill={COMPANY_COLORS[row.company] || FALLBACK} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}
